import { defineComponent, h } from 'vue';
import { QBtn, QIcon, QInput, QDialog } from 'quasar';
import { AgGridVue } from 'ag-grid-vue3';
import utils from '../../../Shared/utils.jsx';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import ControlPicker from './controlPicker.jsx';
import FastGridDesignerActionCell from './FastGridDesignerActionCell.jsx';

function hasNonEmptyValue(text) {
    if (!text)
        return false;

    if (typeof text === 'string')
        if (!text.trim())
            return false;

    return true;
}

function formatDesignerCellValue(data, field) {
    if (!data || !(field in data))
        return '';

    const cell = data[field];
    if (cell === null || typeof cell === 'undefined')
        return '';
    if (typeof cell === 'string')
        return cell;
    if (typeof cell === 'boolean')
        return cell ? 'true' : 'false';
    if (typeof cell === 'number')
        return cell;
    return JSON.stringify(cell);
}

const FASTGRID_COMMAND_TYPE =
    '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_FastGrid_Command';

export default defineComponent({
    name: 'fast-grid-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        AgGridVue,
        QBtn,
        QIcon,
        QInput,
        QDialog,
        ControlPicker,
        FastGridDesignerActionCell,
    },
    data() {
        return {
            schema: null,
            model: null,
            column_defs: [],
            searchText: '',
            gridApi: null,
            previewRows: [],
            /** Bumped on structural column changes to force AgGridVue remount. */
            agGridRemountKey: 0,
        };
    },
    created() {
        this._columnExprCache = new WeakMap();
        this._rebuildingColumnDefs = false;
        this._columnDefsDirty = false;
        this._lastOwnedSelectedModel = null;
        this._lastColumnStructureKey = null;
    },
    mounted() {
        this.Refresh();
    },
    watch: {
        // Explicit computed signature — more reliable than deep-watching a prop path
        // when ColumnDefs is mutated in place from the canvas or PropertyGrid.
        columnDefsSyncKey() {
            this.scheduleColumnDefsApply();
        },
        'controlData.SelectOptions': {
            handler() {
                this.scheduleColumnDefsApply();
            },
            deep: true,
        },
        showAgGrid(val) {
            if (!val)
                this.gridApi = null;
        },
        selected$() {
            this.trackOwnedSelection();
            this.$nextTick(() => this.refreshSelectionPaint());
        },
        'Root.SelectedModel'() {
            this.trackOwnedSelection();
            this.$nextTick(() => this.refreshSelectionPaint());
        },
    },
    computed: {
        /** Grid stays in "selected" mode when a column is active (SelectedModel is the column). */
        selected$() {
            return this.Root.SelectedNode && this.Root.SelectedNode === this;
        },
        Model() {
            return this.model;
        },
        previewRowData() {
            return this.previewRows;
        },
        showAgGrid() {
            return !this.controlData.EnableDynamicColumns
                && Array.isArray(this.controlData.ColumnDefs)
                && this.controlData.ColumnDefs.length > 0;
        },
        /**
         * Tracks ColumnDefs content that must appear in the designer AG Grid.
         * Reading these fields creates reactive deps so add/remove/property edits all sync.
         */
        columnDefsSyncKey() {
            const cols = this.controlData?.ColumnDefs;
            if (!Array.isArray(cols))
                return 'none';

            const parts = [String(cols.length)];
            for (let i = 0; i < cols.length; i++) {
                const c = cols[i];
                if (!c) {
                    parts.push('null');
                    continue;
                }
                const btns = Array.isArray(c.CommandButtons) ? c.CommandButtons : [];
                const btnKey = btns.map((b) => [
                    b?.$objectId || '',
                    b?.Icon || '',
                    b?.Color || '',
                ].join(':')).join(',');
                parts.push([
                    c.$objectId || '',
                    c.Field || '',
                    c.Name || '',
                    c.DisplayName || '',
                    c.Visible === false ? '0' : '1',
                    c.Align || '',
                    c.ActionOnlyColumn ? '1' : '0',
                    c.Width || '',
                    c.CellFilter || '',
                    c.Condition || '',
                    String(btns.length),
                    btnKey,
                ].join('/'));
            }
            return parts.join('~');
        },
        columnStructureKey() {
            const cols = this.controlData?.ColumnDefs;
            if (!Array.isArray(cols))
                return '0';

            return cols.length + ':' + cols.map((c, i) => {
                const field = c?.Field || `col${i}`;
                const colId = c?.ActionOnlyColumn
                    ? (c.Name || field || `action-${i}`)
                    : (c?.Name || field);
                return [
                    c?.$objectId || '',
                    field,
                    c?.Name || '',
                    c?.ActionOnlyColumn ? '1' : '0',
                    Array.isArray(c?.CommandButtons) ? c.CommandButtons.length : 0,
                    colId,
                ].join('/');
            }).join('|');
        },
        usesVerticalFill() {
            return this.controlData.SizeOptions?.Height?.Mode === 'Fill';
        },
        selectedColumnIndex() {
            const m = this.Root?.SelectedModel;
            const cols = this.controlData.ColumnDefs;
            if (!m || !Array.isArray(cols))
                return -1;
            return cols.indexOf(m);
        },
        selectedColId() {
            const index = this.selectedColumnIndex;
            if (index < 0)
                return null;
            return this.getColumnColId(this.controlData.ColumnDefs[index], index);
        },
        designerRowSelection() {
            if (!this.controlData.SelectOptions?.Enable)
                return undefined;

            const multi = this.controlData.SelectOptions.MultiSelect;
            if (multi) {
                return {
                    mode: 'multiRow',
                    checkboxes: true,
                    headerCheckbox: this.controlData.RowModelType !== 'serverSide',
                    enableClickSelection: false,
                    hideDisabledCheckboxes: this.controlData.SelectOptions.ShowDisabledCheckboxes === true,
                };
            }

            return {
                mode: 'singleRow',
                checkboxes: true,
                enableClickSelection: false,
            };
        },
    },
    methods: {
        selectColumn(e, index) {
            this.Root.SelectNode(this, this.controlData.ColumnDefs[index]);
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault?.();
        },
        ensureCommandButtonSchema(btn) {
            if (btn && !btn.$typeSchema)
                btn.$typeSchema = FASTGRID_COMMAND_TYPE;
            if (btn && !btn.$objectId)
                btn.$objectId = utils.generateUUID();
        },
        ensureAllCommandButtonSchemas() {
            const cols = this.controlData.ColumnDefs;
            if (!cols) return;
            for (const cdef of cols) {
                if (!cdef.ActionOnlyColumn || !Array.isArray(cdef.CommandButtons)) continue;
                for (const btn of cdef.CommandButtons)
                    this.ensureCommandButtonSchema(btn);
            }
        },
        selectCommandButton(e, btn) {
            this.ensureCommandButtonSchema(btn);
            this.Root.SelectNode(this, btn);
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault?.();
        },
        addCommandButton(e, columnDef) {
            if (!columnDef.CommandButtons || !Array.isArray(columnDef.CommandButtons))
                columnDef.CommandButtons = [];
            const newBtn = {
                $objectId: utils.generateUUID(),
                $typeSchema: FASTGRID_COMMAND_TYPE,
                Icon: 'mdi-circle-outline',
                Color: 'primary',
                Actions: [],
            };
            columnDef.CommandButtons.push(newBtn);
            this.Root.SelectNode(this, newBtn);
            this.scheduleColumnDefsApply();
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault?.();
        },
        deleteColumn(e, index) {
            const cols = this.controlData.ColumnDefs;
            if (!Array.isArray(cols) || index < 0 || index >= cols.length)
                return;

            const removed = cols[index];
            const selected = this.Root?.SelectedModel;
            const removingSelected = selected === removed
                || (Array.isArray(removed?.CommandButtons) && removed.CommandButtons.indexOf(selected) >= 0);

            cols.splice(index, 1);

            if (removingSelected) {
                const neighbor = cols[index] || cols[index - 1];
                if (neighbor)
                    this.Root.SelectNode(this, neighbor);
                else
                    this.Root.SelectNode(this);
            }
            this.scheduleColumnDefsApply();
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault?.();
        },
        addColumn(e) {
            if (!this.controlData.ColumnDefs)
                this.controlData.ColumnDefs = [];

            this.controlData.ColumnDefs.push({
                $typeSchema: '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_FastGrid_ColumnDef',
                $objectId: utils.generateUUID(),
                Align: 'Left',
                DisplayName: '',
                Field: 'field' + (this.controlData.ColumnDefs.length + 1),
                Sortable: true,
                Visible: true,
                Width: 'Auto',
            });
            this.Root.SelectNode(this, this.controlData.ColumnDefs[this.controlData.ColumnDefs.length - 1]);
            this.scheduleColumnDefsApply();
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault?.();
        },
        addGridHeaderControl(e, side) {
            const key = side === 'left' ? 'LeftGridHeaderControls' : 'GridHeaderControls';
            if (!this.controlData[key])
                this.controlData[key] = [];
            this.pickertarget = this.controlData[key];
            this.pickervisible = true;
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault?.();
        },
        async Refresh() {
            if (this.controlData.SchemaURL)
                try {
                    this.schema = await utils.schema.get(this.controlData.SchemaURL);
                }
                catch (e) {
                    utils.warn('Design FastGrid schemaurl ' + this.controlData.SchemaURL + ' failed to load: ' + e);
                    this.schema = null;
                }

            if (this.controlData.DataType == 'URL' && this.controlData.DataURL)
                try {
                    this.model = await utils.api.get(this.controlData.DataURL);
                }
                catch (e) {
                    utils.warn('Design FastGrid DataURL ' + this.controlData.DataURL + ' failed to load: ' + e);
                    this.model = [{}];
                }
            else if (this.controlData.DataType == 'Raw' && this.controlData.Data && Array.isArray(this.controlData.Data)) {
                this.model = this.controlData.Data;
            }
            else
                this.model = [{}];

            this.preparePreviewRows();
            this.scheduleColumnDefsApply();
        },
        preparePreviewRows() {
            if (!this.model || !Array.isArray(this.model)) {
                this.previewRows = [];
                return;
            }

            const rows = [];
            let c = this.model.length;
            if (c > 10) c = 10;
            for (let i = 0; i < c; i++) {
                const row = this.model[i];
                if (!row.$objectId)
                    row.$objectId = utils.generateUUID();
                rows.push(row);
            }
            this.previewRows = rows;
        },
        getDesignerColumnExpressions(cd, field) {
            const sig = `${field}|${cd.CellFilter || ''}|${cd.Condition || ''}|${cd.ItemModelName || 'item'}|${cd.RowModelName || 'row'}`;
            let cached = this._columnExprCache.get(cd);
            if (cached && cached.sig === sig)
                return cached;

            cached = { sig, cellfilter_expn: null, condition_expn: null };
            if (cd.CellFilter) {
                const filter = `{{ row["${field}"] | ${cd.CellFilter} }}`;
                cached.cellfilter_expn = utils.compile(this, filter, false, ['row', cd.ItemModelName || 'item']);
            }
            if (cd.Condition)
                cached.condition_expn = utils.compileExpression(this, cd.Condition, cd.RowModelName || 'row');

            this._columnExprCache.set(cd, cached);
            return cached;
        },
        getColumnDefTitle(cdef) {
            if (hasNonEmptyValue(cdef.DisplayName))
                return cdef.DisplayName;
            else
                return cdef.Field || cdef.Name || 'Unnamed';
        },
        createDesignerValueFormatter(cd, field) {
            const self = this;
            const field_extractor = new Function('f', `return f != null && typeof f === 'object' && "${field}" in f ? f["${field}"] : null;`);
            const { cellfilter_expn, condition_expn } = this.getDesignerColumnExpressions(cd, field);

            const evaluateFormatted = (data) => {
                if (!data || !cellfilter_expn)
                    return '';
                try {
                    return utils.evaluate(cellfilter_expn, self, false, null, false, [data, field_extractor(data)], true);
                }
                catch (e) {
                    utils.warn(`Design FastGrid CellFilter failed for field ${field}: ${cd.CellFilter}`, e);
                    return formatDesignerCellValue(data, field);
                }
            };

            if (cellfilter_expn && condition_expn) {
                return (params) => {
                    if (!params.data)
                        return '';
                    try {
                        return utils.evaluate(condition_expn, self, false, null, false, [params.data, field_extractor(params.data)], true)
                            ? evaluateFormatted(params.data)
                            : '';
                    }
                    catch (e) {
                        return formatDesignerCellValue(params.data, field);
                    }
                };
            }

            if (cellfilter_expn)
                return (params) => evaluateFormatted(params.data);

            if (condition_expn) {
                return (params) => {
                    if (!params.data)
                        return '';
                    try {
                        return utils.evaluate(condition_expn, self, false, null, false, [params.data, field_extractor(params.data)], true)
                            ? formatDesignerCellValue(params.data, field)
                            : '';
                    }
                    catch (e) {
                        return formatDesignerCellValue(params.data, field);
                    }
                };
            }

            return (params) => formatDesignerCellValue(params.data, field);
        },
        getColumnColId(cd, index) {
            const field = cd.Field || `col${index}`;
            if (cd.ActionOnlyColumn)
                return cd.Name || field || `action-${index}`;
            return cd.Name || field;
        },
        getHeaderAlignClass(cd) {
            if (cd.Align === 'Right')
                return 'ag-right-aligned-header';
            if (cd.Align === 'Center')
                return 'ag-center-aligned-header';
            return undefined;
        },
        updateSelectedHeaderHighlight() {
            const root = this.$refs.agGridRef?.$el;
            if (!root)
                return;

            root.querySelectorAll('.ag-header-cell.fast-grid-dsgn-col-selected')
                .forEach((el) => el.classList.remove('fast-grid-dsgn-col-selected'));

            const colId = this.selectedColId;
            if (!colId)
                return;

            const escaped = (typeof CSS !== 'undefined' && CSS.escape)
                ? CSS.escape(colId)
                : colId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
            const cell = root.querySelector(`.ag-header-cell[col-id="${escaped}"]`);
            if (cell)
                cell.classList.add('fast-grid-dsgn-col-selected');
        },
        refreshSelectionPaint() {
            this.updateSelectedHeaderHighlight();
            if (this.isGridApiLive())
                this.gridApi.refreshCells({ force: true });
        },
        isGridApiLive() {
            const api = this.gridApi;
            if (!api)
                return false;
            if (typeof api.isDestroyed === 'function' && api.isDestroyed())
                return false;
            return true;
        },
        findOwnedSubModelKind(model) {
            const cols = this.controlData.ColumnDefs;
            if (!model || !Array.isArray(cols))
                return null;
            if (cols.indexOf(model) >= 0)
                return 'column';
            for (let i = 0; i < cols.length; i++) {
                const btns = cols[i].CommandButtons;
                if (Array.isArray(btns) && btns.indexOf(model) >= 0)
                    return 'command';
            }
            return null;
        },
        trackOwnedSelection() {
            if (this.Root?.SelectedNode !== this) {
                this._lastOwnedSelectedModel = null;
                return;
            }

            const m = this.Root?.SelectedModel;
            if (!m || m === this.designmodel) {
                this._lastOwnedSelectedModel = null;
                return;
            }

            if (this.findOwnedSubModelKind(m))
                this._lastOwnedSelectedModel = m;
            // Nested PropertyGrid models keep the previous owned column/command ref.
        },
        retargetOrphanedSelection() {
            this.trackOwnedSelection();

            if (this.Root?.SelectedNode !== this)
                return;

            const m = this.Root?.SelectedModel;
            if (!m || m === this.designmodel)
                return;

            if (this.findOwnedSubModelKind(m))
                return;

            // Nested PropertyGrid navigation: keep selection while the last owned
            // column/command is still present in ColumnDefs.
            if (this._lastOwnedSelectedModel
                && this._lastOwnedSelectedModel !== m
                && this.findOwnedSubModelKind(this._lastOwnedSelectedModel))
                return;

            const columnSchema = '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_FastGrid_ColumnDef';
            const looksLikeOwnedType = m.$typeSchema === columnSchema || m.$typeSchema === FASTGRID_COMMAND_TYPE;
            const ownedRemoved = this._lastOwnedSelectedModel === m
                || (this._lastOwnedSelectedModel && !this.findOwnedSubModelKind(this._lastOwnedSelectedModel));

            if (ownedRemoved || looksLikeOwnedType) {
                this._lastOwnedSelectedModel = null;
                this.Root.SelectNode(this);
            }
        },
        findColumnDefIndexByColId(colId) {
            const cols = this.controlData.ColumnDefs;
            if (!colId || !Array.isArray(cols))
                return -1;

            for (let i = 0; i < cols.length; i++) {
                if (this.getColumnColId(cols[i], i) === colId)
                    return i;
            }
            return -1;
        },
        onColumnHeaderClicked(event) {
            const index = this.findColumnDefIndexByColId(event.column?.getColId?.());
            if (index < 0)
                return;

            this.Root.SelectNode(this, this.controlData.ColumnDefs[index]);
            if (event.event) {
                event.event.stopPropagation();
                event.event.preventDefault();
            }
        },
        deleteSelectedColumn(e) {
            const index = this.selectedColumnIndex;
            if (index >= 0)
                this.deleteColumn(e, index);
        },
        buildDesignerColumnDefs() {
            const cols = this.controlData.ColumnDefs;
            if (!cols || !Array.isArray(cols) || cols.length === 0)
                return [];

            const defs = [];
            for (let i = 0; i < cols.length; i++) {
                const cd = cols[i];
                const field = cd.Field || `col${i}`;
                const colId = this.getColumnColId(cd, i);
                const headerName = this.getColumnDefTitle(cd);

                if (cd.ActionOnlyColumn) {
                    const btnCount = Array.isArray(cd.CommandButtons) ? cd.CommandButtons.length : 0;
                    const def = {
                        colId,
                        headerName,
                        headerClass: this.getHeaderAlignClass(cd),
                        sortable: false,
                        resizable: true,
                        suppressHeaderFilterButton: true,
                        suppressHeaderMenuButton: true,
                        cellRenderer: 'FastGridDesignerActionCell',
                        cellRendererParams: {
                            callcorp: {
                                root: this.root,
                                columnDef: cd,
                                gridComponent: this,
                            },
                        },
                        hide: cd.Visible === false,
                        minWidth: Math.max(80, btnCount * 36 + 24),
                        cellClass: 'fast-grid-dsgn-action-col-cell',
                    };
                    if (cd.Width > 0)
                        def.width = parseInt(cd.Width);
                    defs.push(def);
                }
                else {
                    const def = {
                        field,
                        colId,
                        headerName,
                        headerClass: this.getHeaderAlignClass(cd),
                        sortable: false,
                        resizable: true,
                        filter: true,
                        hide: cd.Visible === false,
                        valueFormatter: this.createDesignerValueFormatter(cd, field),
                    };

                    if (cd.Align === 'Right')
                        def.cellClass = 'right-aligned-cell';
                    else if (cd.Align === 'Center')
                        def.cellClass = 'center-aligned-cell';

                    defs.push(def);
                }
            }
            return defs;
        },
        scheduleColumnDefsApply() {
            if (this._rebuildingColumnDefs) {
                this._columnDefsDirty = true;
                return;
            }
            this._rebuildingColumnDefs = true;
            this.applyColumnDefs();
        },
        applyColumnDefs() {
            const scrollLeft = this.getHorizontalScrollPosition();
            try {
                this.ensureAllCommandButtonSchemas();
                const defs = this.buildDesignerColumnDefs();
                this.column_defs = defs;

                const structureKey = this.columnStructureKey;
                const structureChanged = structureKey !== this._lastColumnStructureKey;
                this._lastColumnStructureKey = structureKey;

                // Structural add/remove: remount AgGridVue so headers/columns always refresh.
                // Property-only edits: update via API when the grid is live.
                if (structureChanged) {
                    this.gridApi = null;
                    this.agGridRemountKey += 1;
                }
                else if (this.isGridApiLive()) {
                    try {
                        this.gridApi.setGridOption('columnDefs', defs);
                    }
                    catch (e) {
                        utils.warn('Design FastGrid setGridOption columnDefs failed', e);
                        this.gridApi = null;
                        this.agGridRemountKey += 1;
                    }
                }

                this.retargetOrphanedSelection();
            }
            finally {
                // Clear the rebuild gate synchronously so a follow-up mutation in the
                // same tick is not dropped; coalesce extras via dirty + nextTick.
                const hadDirty = this._columnDefsDirty;
                this._columnDefsDirty = false;
                this._rebuildingColumnDefs = false;

                this.$nextTick(() => {
                    this.setHorizontalScrollPosition(scrollLeft);
                    this.updateSelectedHeaderHighlight();
                    if (hadDirty)
                        this.scheduleColumnDefsApply();
                });
            }
        },
        getHorizontalScrollPosition() {
            const viewport = this.$refs.agGridRef?.$el?.querySelector('.ag-body-horizontal-scroll-viewport');
            return viewport?.scrollLeft ?? 0;
        },
        setHorizontalScrollPosition(scrollLeft) {
            const viewport = this.$refs.agGridRef?.$el?.querySelector('.ag-body-horizontal-scroll-viewport');
            if (viewport)
                viewport.scrollLeft = scrollLeft;
        },
        onGridReady(params) {
            this.gridApi = params.api;

            const nestedDiv = this.$refs.agGridRef?.$el?.children?.[0];
            if (nestedDiv && nestedDiv.tagName === 'DIV')
                nestedDiv.classList.add('ag-grid-flex-layout');

            this.$nextTick(() => this.refreshSelectionPaint());
        },
        onGridPreDestroyed() {
            this.gridApi = null;
        },
        onColumnsUiUpdated() {
            this.updateSelectedHeaderHighlight();
        },
        renderDesignerHeaderControls(side) {
            const key = side === 'left' ? 'LeftGridHeaderControls' : 'GridHeaderControls';
            const controls = this.controlData[key];
            const items = [];

            if (controls && controls.length > 0) {
                for (let j = 0; j < controls.length; j++) {
                    const control = controls[j];
                    const DynamicControl = utils.getDesignerComponent(control, null);

                    if (!control.$objectId)
                        control.$objectId = utils.generateUUID();

                    items.push(
                        <DynamicControl
                            key={control.$objectId}
                            type={control.ControlType}
                            name={control.ControlData ? control.ControlData.Name : ''}
                            root={this.root}
                            designmodel={control}
                            parentType="HorizontalStack"
                            controlData={control.ControlData}
                            controlURL={control.ControlURL}
                        />
                    );
                }
            }

            if (this.selected$) {
                const label = side === 'left' ? 'Add control to left header' : 'Add control to right header';
                items.push(
                    utils.tt(label,
                        <QBtn
                            size="sm"
                            flat
                            icon="mdi-plus-circle"
                            color="blue-8"
                            class="designer-container-selected-clickableitem"
                            onClick={(e) => this.addGridHeaderControl(e, side)}
                        />)
                );
            }

            return items;
        },
        renderDesignerGridHeaders() {
            const items = [];

            if (this.controlData.EnableFilter) {
                items.push(
                    <QInput
                        key="quicksearch"
                        class="q-mb-none quick-search-input"
                        dense
                        hide-hint
                        hide-bottom-space
                        readonly
                        placeholder="Quick Search"
                        modelValue={this.searchText}
                        v-slots={{ prepend: () => <QIcon name="mdi-magnify" /> }}
                    />
                );
            }
            else if (this.controlData.ShowJumpto) {
                items.push(
                    <QInput
                        key="jumpto"
                        style="max-width: 300px;"
                        class="q-mb-none"
                        dense
                        hide-hint
                        hide-bottom-space
                        readonly
                        disable
                        placeholder="Jump to..."
                        v-slots={{ prepend: () => <QIcon name="mdi-magnify" /> }}
                    />
                );
            }

            const leftHeader = this.renderDesignerHeaderControls('left');
            if (leftHeader.length > 0)
                items.push(<span key="left-header" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>{leftHeader}</span>);

            items.push(<span key="spacer" style="flex-grow: 1;"></span>);

            const rightHeader = this.renderDesignerHeaderControls('right');
            if (rightHeader.length > 0)
                items.push(<span key="right-header" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>{rightHeader}</span>);

            if (this.selected$) {
                items.push(
                    utils.tt('Add column',
                        <QBtn
                            key="add-column"
                            size="sm"
                            flat
                            icon="mdi-plus-circle"
                            color="pink-9"
                            class="designer-container-selected-clickableitem"
                            style={{ flexShrink: 0 }}
                            onClick={(e) => this.addColumn(e)}
                        />)
                );

                if (this.selectedColumnIndex >= 0) {
                    items.push(
                        utils.tt('Delete selected column',
                            <QBtn
                                key="delete-column"
                                size="sm"
                                flat
                                icon="mdi-close"
                                color="blue"
                                class="designer-container-selected-clickableitem"
                                style={{ flexShrink: 0 }}
                                onClick={(e) => this.deleteSelectedColumn(e)}
                            />)
                    );
                }
            }

            if (!this.controlData.HideDefaultGridControls) {
                items.push(
                    <QBtn key="autosize" flat dense disable icon="mdi-arrow-left-right-bold" title="Size columns to content" />,
                    <QBtn key="fit" flat dense disable icon="mdi-arrow-expand-horizontal" title="Size columns to fit" />,
                );
                if (!this.controlData.HeaderOptions?.HideRefreshIcon)
                    items.push(<QBtn key="refresh" flat dense disable icon="mdi-cached" title="Refresh" />);
            }

            if (!this.controlData.DisableGridMenu) {
                items.push(
                    <QBtn key="grid-menu" flat dense disable icon="mdi-menu" title="Grid menu" />
                );
            }

            return (
                <div class="gridHeaderButtons">
                    {items}
                </div>
            );
        },
        renderGridBody() {
            if (this.model === null)
                return <div style={{ padding: '8px' }}>Loading...</div>;

            if (this.controlData.EnableDynamicColumns) {
                return (
                    <div style={{ padding: '16px', color: '#666', fontStyle: 'italic' }}>
                        Dynamic columns preview not available in designer. Configure ColumnTemplates and DynamicColumns in the property panel.
                    </div>
                );
            }

            if (!this.showAgGrid) {
                return (
                    <div style={{ padding: '16px', color: '#666', fontStyle: 'italic', flexShrink: 0 }}>
                        No columns defined. Select this grid and use the add-column button in the toolbar to create columns.
                    </div>
                );
            }

            return (
                <div
                    class="fast-grid-dsgn-body"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 1,
                    }}>
                    <AgGridVue
                        key={'fast-grid-dsgn-' + this.agGridRemountKey}
                        ref="agGridRef"
                        class={{ 'ag-theme-alpine': true }}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minHeight: this.usesVerticalFill ? 0 : '120px',
                            width: '100%',
                        }}
                        rowData={this.previewRowData}
                        columnDefs={this.column_defs}
                        domLayout={this.usesVerticalFill ? 'normal' : 'autoHeight'}
                        rowSelection={this.designerRowSelection}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        enableCellTextSelection={true}
                        maintainColumnOrder={true}
                        pagination={false}
                        onGridReady={this.onGridReady}
                        onGridPreDestroyed={this.onGridPreDestroyed}
                        onNewColumnsLoaded={this.onColumnsUiUpdated}
                        onDisplayedColumnsChanged={this.onColumnsUiUpdated}
                        onColumnHeaderClicked={this.onColumnHeaderClicked}
                    />
                </div>
            );
        },
    },
    render() {
        const style = {
            overflow: this.usesVerticalFill ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: '8px',
            position: 'relative',
            borderStyle: 'solid',
            borderWidth: '1px',
            minHeight: 0,
            ...utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent),
        };

        try {
            return (
                <div
                    class={{
                        'c-FastGrid': true,
                        'ag-theme-alpine': true,
                        'designer-container': true,
                        'designer-form': true,
                        selected: this.selected$,
                        [`c-name-${this.name || 'unnamed'}`]: true,
                    }}
                    style={style}
                    onClick={(e) => this.handleClick(e)}>
                    <div class={{ 'designer-container-hovering': true, 'designer-container-selected-clickthrough': this.selected$ }}></div>

                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                    {this.renderDesignerGridHeaders()}
                    {this.renderGridBody()}

                    {this.generatePicker(h)}
                    </div>
                </div>
            );
        }
        catch (e) {
            utils.error('FastGrid designer render failed', e);
            return <div>FastGrid Failed to Render {String(e)}</div>;
        }
    },
});
